import { Text } from '@bechtel/gds';
import { UiTextEnum } from '@bechtel/sr-types';
import 'ag-grid-community/styles/ag-grid.css'; // Core grid CSS, always needed
import 'ag-grid-community/styles/ag-theme-alpine.css'; // Optional theme CSS
import { useCallback, useEffect, useState } from 'react';
import { useLanguage } from '~/components';
import { DEFAULT_ROW_HEIGHT } from '../../types';

export const StatusBarLeft = (props) => {
  const [displayedTopRow, setDisplayedTopRow] = useState<number>(0);
  const [displayedBottomRow, setDisplayedBottomRow] = useState<number>(0);
  const [visibleRows, setVisibleRows] = useState<number>(0);
  const { getMultiLingualTextByKey } = useLanguage();
  const rowHeight = props?.api?.getColumnDefs()?.[0]?.cellEditorParams?.rowHeight ?? DEFAULT_ROW_HEIGHT;

  const updatePageStats = useCallback(
    (api) => {
      try {
        const pixelRange = api.getVerticalPixelRange();
        // calculating the number of visible rows based on rowHeight and pixel range
        const numberOfVisibleRows = Math.ceil(pixelRange?.bottom / rowHeight);

        // calculating the top row index
        const topRowIndex = Math.floor(pixelRange.top / rowHeight);
        // calculating the bottom row index by dividing pixelrange by rowheight.
        // added api.getDisplayedRowCount() - 1 in Math.min for the cases when number of rows are less than the pixelRange
        const bottomRowIndex = Math.min(api.getDisplayedRowCount() - 1, Math.floor(pixelRange.bottom / rowHeight));

        setVisibleRows(numberOfVisibleRows);
        setDisplayedTopRow(topRowIndex);
        setDisplayedBottomRow(bottomRowIndex);
      } catch (error) {
        console.error('Error updating page stats:', error);
      }
    },
    [rowHeight]
  );

  const onFilterChanged = useCallback(() => {
    if (props.api) {
      // if filtered rows is less than the visible rows, we are displaying the filtered row count
      if (props.api.getDisplayedRowCount() < visibleRows) {
        const totalRowsUpdated = props.api.getDisplayedRowCount();
        setDisplayedBottomRow(totalRowsUpdated);
      } else {
        // Else if filtered rows is more than the visible rows,
        // we are re-calculating the visible rows, top row index and bottom row index
        updatePageStats(props.api);
      }
    }
  }, [props.api, updatePageStats, visibleRows]);

  useEffect(() => {
    const onFilterChangedHandler = () => {
      // setTimeout for asynchronous operations
      setTimeout(() => {
        if (props.api) {
          onFilterChanged();
        }
      }, 200);
    };

    const updateStatsHandler = () => {
      // setTimeout for asynchronous operations
      setTimeout(() => {
        if (props.api) {
          updatePageStats(props.api);
        }
      }, 200);
    };
    updateStatsHandler();
    props.api.addEventListener('modelUpdated', updateStatsHandler);
    props.api.addEventListener('bodyScroll', updateStatsHandler);
    props.api.addEventListener('filterChanged', onFilterChangedHandler);
    return () => {
      if (!props.api.isDestroyed()) {
        props.api.removeEventListener('modelUpdated', updateStatsHandler);
        props.api.removeEventListener('bodyScroll', updateStatsHandler);
        props.api.removeEventListener('filterChanged', onFilterChangedHandler);
      }
    };
  }, [onFilterChanged, props.api, updatePageStats]);

  return (
    <div className="ag-status-bar-left">
      {!isNaN(displayedTopRow) && !isNaN(displayedBottomRow) && displayedBottomRow > 0 && props.total > 0 && (
        <div className="ag-status-name-value ag-status-panel ag-status-panel-total-row-count">
          <Text size="250" weight={'600'}>
            {getMultiLingualTextByKey(UiTextEnum.SHOWING_ROWS, [
              String(displayedTopRow + 1),
              String(displayedBottomRow + 1),
            ])}
          </Text>
        </div>
      )}
    </div>
  );
};

export const StatusBarRight = (props) => {
  const { getMultiLingualTextByKey } = useLanguage();

  return (
    <div className="ag-status-bar-right">
      <div className="ag-status-name-value ag-status-panel ag-status-panel-total-row-count">
        {props.total > 0 && (
          <Text size="250" weight={'600'}>
            {getMultiLingualTextByKey(UiTextEnum.TOTAL_ROWS, [String(props.total)])}
          </Text>
        )}
      </div>
    </div>
  );
};
